/**
 * Snapshot Parser
 *
 * Parses Playwright's accessibility snapshot YAML-like format to extract
 * element information for stable selector derivation.
 *
 * The snapshot format from Playwright's `ariaSnapshot({ mode: 'ai' })` looks like:
 * ```yaml
 * - navigation "Main":
 *   - link "Home" [ref=e1]
 *   - link "About" [ref=e2]
 * - main:
 *   - heading "Welcome" [level=1] [ref=e3]
 *   - paragraph:
 *     - text "Hello world"
 *   - button "Submit" [ref=e4]
 * ```
 */

import type { ElementInfo, FrameContext } from '@chatsdet/types';

/**
 * Line parsed from the snapshot YAML.
 */
export interface ParsedLine {
  indent: number;
  role: string;
  name?: string;
  ref?: string;
  centerX?: number;
  centerY?: number;
  attributes: Record<string, string>;
  text?: string;
}

/**
 * Parse a single line from the snapshot.
 */
function parseLine(line: string): ParsedLine | null {
  if (!line.trim()) return null;

  // Calculate indent (2 spaces per level)
  const indent = line.search(/\S/);
  const content = line.trim();

  // Skip empty or dash-only lines
  if (!content || content === '-') return null;

  // Remove leading dash if present
  let cleanContent = content.startsWith('- ') ? content.slice(2) : content;
  if (!cleanContent) return null;

  // Strip YAML single-quote wrapping. When element text contains colons
  // (e.g. "Rows per page: 25"), YAML serializes the element key as:
  //   'combobox "Rows per page: 25" [ref=e542]': "25"
  // Unwrap only the quoted key and preserve any trailing input value.
  if (cleanContent.startsWith("'")) {
    const quotedKeyEnd = cleanContent.indexOf("':");
    if (quotedKeyEnd !== -1) {
      cleanContent = cleanContent.slice(1, quotedKeyEnd) + cleanContent.slice(quotedKeyEnd + 1);
    } else if (cleanContent.endsWith("'")) {
      cleanContent = cleanContent.slice(1, -1);
    }
  }

  // Extract ref and optional center coordinates: [ref=e1] or [ref=e1 center=450,320]
  const refMatch = cleanContent.match(/\[ref=(\w+)(?:\s+center=(\d+),(\d+))?\]/);
  const ref = refMatch?.[1];
  const centerX = refMatch?.[2] ? parseInt(refMatch[2], 10) : undefined;
  const centerY = refMatch?.[3] ? parseInt(refMatch[3], 10) : undefined;

  // Remove all bracket attributes for cleaner parsing
  const withoutBrackets = cleanContent.replace(/\s*\[[^\]]+\]/g, '').trim();

  // Handle sub-property lines like `/placeholder: Enter name`
  if (cleanContent.startsWith('/')) {
    const colonIdx = cleanContent.indexOf(':');
    if (colonIdx !== -1) {
      const key = cleanContent.slice(1, colonIdx).trim();
      const value = cleanContent.slice(colonIdx + 1).trim();
      return { indent, role: '_property', attributes: { [key]: value } };
    }
  }

  // Handle text content lines (no role, just text)
  if (withoutBrackets.startsWith('"') && withoutBrackets.endsWith('"')) {
    return {
      indent,
      role: 'text',
      text: withoutBrackets.slice(1, -1),
      attributes: {},
    };
  }

  // Parse role and optional name: `button "Submit"` or `button:`
  // Also handles `text "content"` format and inline text after colon: `button "X": Click me`
  const roleNameMatch = withoutBrackets.match(/^(\w+)(?:\s+"([^"]+)")?(?::(?:\s+(.+))?)?$/);
  if (!roleNameMatch) {
    // Might be a plain text node or malformed line
    return {
      indent,
      role: 'generic',
      text: withoutBrackets,
      attributes: {},
    };
  }

  const role = roleNameMatch[1];
  const name = roleNameMatch[2];
  const inlineText = roleNameMatch[3];

  // Extract other attributes
  const attributes: Record<string, string> = {};
  const attrMatches = cleanContent.matchAll(/\[(\w+)=([^\]]+)\]/g);
  for (const match of attrMatches) {
    const [, key, value] = match;
    if (key !== 'ref') {
      attributes[key] = value;
    }
  }

  return {
    indent,
    role,
    name,
    ref,
    centerX,
    centerY,
    attributes,
    text: inlineText,
  };
}

/**
 * Collect sub-property lines (e.g. `/placeholder: ...`) from children of an element.
 */
function collectSubProperties(
  elementIndex: number,
  allParsed: ParsedLine[],
  elementIndent: number
): Record<string, string> {
  const props: Record<string, string> = {};
  for (let i = elementIndex + 1; i < allParsed.length; i++) {
    if (allParsed[i].indent <= elementIndent) break;
    if (allParsed[i].role === '_property') {
      Object.assign(props, allParsed[i].attributes);
    }
  }
  return props;
}

/** Roles where child headings typically name the container */
const UNIQUE_ROLES = ['dialog', 'alertdialog', 'form', 'article', 'region'];

/**
 * Select the best parent for disambiguation.
 * Prefers unique roles (dialog) and named elements over unnamed ones.
 *
 * @param parentStack - Stack of parent elements from root to closest
 * @returns The best parent for disambiguation, or undefined if none suitable
 */
function selectBestParent(parentStack: ParsedLine[]): ParsedLine | undefined {
  const reversed = [...parentStack].reverse();

  // First: prefer unique roles WITH names (most specific)
  for (const p of reversed) {
    if (UNIQUE_ROLES.includes(p.role) && p.name) {
      return p;
    }
  }

  // Second: prefer any named parent (more specific than unnamed)
  for (const p of reversed) {
    if (p.name && p.role !== 'generic' && p.role !== 'paragraph' && p.role !== 'text') {
      return p;
    }
  }

  // Third: prefer unique roles even without names
  for (const p of reversed) {
    if (UNIQUE_ROLES.includes(p.role)) {
      return p;
    }
  }

  // Last resort: any non-generic parent
  return reversed.find((p) => p.role !== 'generic' && p.role !== 'paragraph' && p.role !== 'text');
}

/**
 * Enrich an unnamed element with text from its direct child paragraph or heading.
 *
 * When an element (e.g. an `article`) has no `accessibleName` and no inline
 * `text`, scans immediate children (indent = parent indent + 2) for `paragraph`
 * or `heading` elements with a `name` or `text` property. Returns the first
 * match so it can be used as `textContent` for selector derivation.
 */
function enrichElementWithChildText(
  elementIndex: number,
  allParsed: ParsedLine[],
  elementIndent: number
): string | undefined {
  for (let i = elementIndex + 1; i < allParsed.length; i++) {
    const child = allParsed[i];
    // Exited the element's scope
    if (child.indent <= elementIndent) break;
    // Only consider direct children (one level deeper)
    if (child.indent !== elementIndent + 2) continue;
    if ((child.role === 'paragraph' || child.role === 'heading') && (child.name || child.text)) {
      return child.name || child.text;
    }
  }
  return undefined;
}

/**
 * Enrich an unnamed container parent with a child heading's name.
 *
 * When selectBestParent picks a UNIQUE_ROLE parent (article, region, form, dialog)
 * that has no accessible name of its own, scans child lines within that parent's
 * indentation scope for a heading element and uses the first heading's name.
 *
 * @param parent - The selected parent ParsedLine (must have no name)
 * @param parentLineIndex - Index of the parent in allLines
 * @param allLines - All parsed lines in the snapshot
 * @returns The heading name and flag, or undefined if no heading found
 */
function enrichParentWithChildHeading(
  parent: ParsedLine,
  parentLineIndex: number,
  allLines: ParsedLine[]
): { name: string; nameFromHeading: true } | undefined {
  // Only enrich unnamed UNIQUE_ROLE parents
  if (parent.name || !UNIQUE_ROLES.includes(parent.role)) {
    return undefined;
  }

  // Scan child lines (deeper indentation) within this parent's scope
  for (let i = parentLineIndex + 1; i < allLines.length; i++) {
    const child = allLines[i];
    // If we've exited the parent's scope (same or lower indent), stop
    if (child.indent <= parent.indent) break;
    // Look for a heading with a name
    if (child.role === 'heading' && child.name) {
      return { name: child.name, nameFromHeading: true };
    }
  }

  return undefined;
}

/**
 * Scan direct children of an element for an `img` role with a name (alt text).
 * Returns the first child img name found, or undefined.
 *
 * In the snapshot, img children appear as:
 *   - img "Alt text" [ref=eN]
 * at indent = elementIndent + 2 (direct children).
 */
function findChildImgAlt(
  elementIndex: number,
  allParsed: ParsedLine[],
  elementIndent: number
): string | undefined {
  for (let i = elementIndex + 1; i < allParsed.length; i++) {
    const child = allParsed[i];
    // Exited the element's scope
    if (child.indent <= elementIndent) break;
    // Only consider direct children (one level deeper)
    if (child.indent !== elementIndent + 2) continue;
    if (child.role === 'img' && child.name) {
      return child.name;
    }
  }
  return undefined;
}

/**
 * Detect if a target element is inside an iframe ancestor.
 *
 * Walks the parent stack to find the nearest `iframe` role. Since the snapshot
 * YAML doesn't include CSS class/id information for iframes, we build a
 * convention-based selector:
 * - If the iframe has a `name` attribute → `iframe[name="..."]`
 * - If only one iframe exists in the entire snapshot → `iframe`
 * - Otherwise → `iframe` (best effort; DOM metadata enrichment may refine this)
 *
 * @param parentStack - Parent elements from root to closest ancestor
 * @param totalIframeCount - Total number of iframe nodes in the full snapshot
 * @returns FrameContext if inside an iframe, undefined otherwise
 */
function detectIframeAncestor(
  parentStack: Array<{ line: ParsedLine; allParsedIndex: number }>,
  _totalIframeCount: number
): FrameContext | undefined {
  // Walk from closest ancestor outward to find the nearest iframe
  for (let i = parentStack.length - 1; i >= 0; i--) {
    const parent = parentStack[i].line;
    if (parent.role === 'iframe') {
      let selector = 'iframe';

      // Use name attribute if available for specificity
      if (parent.name) {
        selector = `iframe[name="${parent.name}"]`;
      } else if (parent.attributes['name']) {
        selector = `iframe[name="${parent.attributes['name']}"]`;
      }
      // When there's only one iframe, bare `iframe` is sufficient

      return {
        type: 'iframe',
        selector,
        name: parent.name ?? parent.attributes['name'],
      };
    }
  }
  return undefined;
}

/**
 * Count the total number of iframe elements in parsed lines.
 */
function countIframes(allParsed: ParsedLine[]): number {
  let count = 0;
  for (const line of allParsed) {
    if (line.role === 'iframe') count++;
  }
  return count;
}

/**
 * Build an ElementInfo from an AncestorChainResult.
 *
 * Applies all enrichment steps: iframe detection, parent selection,
 * heading enrichment, child text/img enrichment, and sub-property collection.
 */
function ancestorChainToElementInfo(result: AncestorChainResult): ElementInfo {
  const { element, elementIndex, ancestors, allParsed, subProperties } = result;

  // Detect iframe ancestor for frameContext
  const parentStack = ancestors.map((a) => ({ line: a.line, allParsedIndex: a.index }));
  const totalIframeCount = countIframes(allParsed);
  const frameContext = detectIframeAncestor(parentStack, totalIframeCount);

  // Find the most relevant parent using smart selection
  const relevantParent = selectBestParent(ancestors.map((a) => a.line));

  // Enrich unnamed container parents with child heading name
  let parentName = relevantParent?.name;
  let parentNameFromHeading: boolean | undefined;

  if (relevantParent && !relevantParent.name) {
    const ancestorEntry = ancestors.find((a) => a.line === relevantParent);
    if (ancestorEntry) {
      const headingInfo = enrichParentWithChildHeading(
        relevantParent,
        ancestorEntry.index,
        allParsed
      );
      if (headingInfo) {
        parentName = headingInfo.name;
        parentNameFromHeading = headingInfo.nameFromHeading;
      }
    }
  }

  // Enrich unnamed elements with child text content
  let textContent = element.text ?? element.name;
  if (!textContent && !element.name) {
    textContent = enrichElementWithChildText(elementIndex, allParsed, element.indent);
  }

  // Find child img alt text for disambiguation of generic containers
  const childImgAlt = findChildImgAlt(elementIndex, allParsed, element.indent);

  return {
    ref: element.ref!,
    role: element.role,
    accessibleName: element.name,
    textContent,
    ariaLabel: element.attributes['aria-label'],
    placeholder: subProperties['placeholder'],
    parentRole: relevantParent?.role,
    parentName,
    parentNameFromHeading,
    centerX: element.centerX,
    centerY: element.centerY,
    childImgAlt,
    frameContext,
  };
}

/**
 * Find element info by ref in the snapshot.
 *
 * Walks through the snapshot lines to find the element with the given ref,
 * also collecting parent context for disambiguation.
 *
 * @param snapshotYaml - The YAML-like snapshot string
 * @param targetRef - The ref to find (e.g., 'e1', 'e5')
 * @returns Element info if found, null otherwise
 */
export function findElementByRef(snapshotYaml: string, targetRef: string): ElementInfo | null {
  const result = findElementWithAncestors(snapshotYaml, targetRef);
  if (!result) return null;
  return ancestorChainToElementInfo(result);
}

/**
 * Result of finding an element with its full ancestor chain.
 * Used by the deterministic locator builder to construct compositional
 * locator chains with scoping (dialog, row, container).
 */
export interface AncestorEntry {
  line: ParsedLine;
  index: number;
}

export interface AncestorChainResult {
  /** The target element */
  element: ParsedLine;
  /** Index of the target in allParsed */
  elementIndex: number;
  /** Ancestors from root to closest parent (does not include target), with indices */
  ancestors: AncestorEntry[];
  /** All parsed lines from the snapshot */
  allParsed: ParsedLine[];
  /** Sub-properties collected from target's children (e.g. placeholder) */
  subProperties: Record<string, string>;
}

/**
 * Find an element by ref and return it with its full ancestor chain.
 *
 * Unlike findElementByRef() which returns a flattened ElementInfo with
 * only the "best" parent, this returns the complete ancestor stack so
 * callers can analyze the full hierarchy for scoping decisions.
 *
 * @param snapshotYaml - The YAML-like snapshot string
 * @param targetRef - The ref to find (e.g., 'e1', 'e5')
 * @returns Ancestor chain result, or null if ref not found
 */
export function findElementWithAncestors(
  snapshotYaml: string,
  targetRef: string
): AncestorChainResult | null {
  const rawLines = snapshotYaml.split('\n');

  const allParsed: ParsedLine[] = [];
  for (const line of rawLines) {
    const parsed = parseLine(line);
    if (parsed) allParsed.push(parsed);
  }

  const parentStack: Array<{ line: ParsedLine; allParsedIndex: number }> = [];
  let targetElement: ParsedLine | null = null;
  let targetIdx = -1;

  for (let idx = 0; idx < allParsed.length; idx++) {
    const parsed = allParsed[idx];

    while (
      parentStack.length > 0 &&
      parentStack[parentStack.length - 1].line.indent >= parsed.indent
    ) {
      parentStack.pop();
    }

    if (parsed.ref === targetRef) {
      targetElement = parsed;
      targetIdx = idx;
      break;
    }

    if (parsed.role !== 'text') {
      parentStack.push({ line: parsed, allParsedIndex: idx });
    }
  }

  if (!targetElement) {
    return null;
  }

  const subProperties = collectSubProperties(targetIdx, allParsed, targetElement.indent);

  return {
    element: targetElement,
    elementIndex: targetIdx,
    ancestors: parentStack.map((p) => ({ line: p.line, index: p.allParsedIndex })),
    allParsed,
    subProperties,
  };
}

/**
 * Extract all elements with refs from the snapshot.
 * Useful for debugging and testing.
 *
 * @param snapshotYaml - The YAML-like snapshot string
 * @returns Array of element info for all elements with refs
 */
export function extractAllElements(snapshotYaml: string): ElementInfo[] {
  const rawLines = snapshotYaml.split('\n');

  const allParsed: ParsedLine[] = [];
  for (const line of rawLines) {
    const parsed = parseLine(line);
    if (parsed) allParsed.push(parsed);
  }

  const parentStack: Array<{ line: ParsedLine; allParsedIndex: number }> = [];
  const elements: ElementInfo[] = [];

  for (let idx = 0; idx < allParsed.length; idx++) {
    const parsed = allParsed[idx];

    while (
      parentStack.length > 0 &&
      parentStack[parentStack.length - 1].line.indent >= parsed.indent
    ) {
      parentStack.pop();
    }

    if (parsed.ref) {
      const subProperties = collectSubProperties(idx, allParsed, parsed.indent);
      elements.push(
        ancestorChainToElementInfo({
          element: parsed,
          elementIndex: idx,
          ancestors: parentStack.map((p) => ({ line: p.line, index: p.allParsedIndex })),
          allParsed,
          subProperties,
        })
      );
    }

    if (parsed.role !== 'text') {
      parentStack.push({ line: parsed, allParsedIndex: idx });
    }
  }

  return elements;
}

/**
 * Known ARIA roles used to split description strings into (name, role) pairs.
 * Ordered by specificity (longer roles first to avoid partial matches).
 */
const KNOWN_ROLES = [
  'progressbar',
  'scrollbar',
  'separator',
  'alertdialog',
  'navigation',
  'application',
  'radiogroup',
  'tabpanel',
  'textbox',
  'combobox',
  'checkbox',
  'treeitem',
  'menuitem',
  'listitem',
  'toolbar',
  'tooltip',
  'heading',
  'spinbutton',
  'article',
  'button',
  'dialog',
  'figure',
  'banner',
  'region',
  'search',
  'status',
  'switch',
  'slider',
  'option',
  'select',
  'meter',
  'radio',
  'table',
  'timer',
  'group',
  'alert',
  'grid',
  'form',
  'cell',
  'link',
  'list',
  'menu',
  'note',
  'main',
  'tree',
  'tab',
  'row',
  'img',
  'log',
];

/**
 * Parse an element description into a (name, role) pair.
 *
 * Supports two patterns:
 * - `"<name> <role>"` — e.g., `"Customer No. combobox"` → name=`"Customer No."`, role=`"combobox"`
 * - `"<role> <name>"` — e.g., `"button Save"` → name=`"Save"`, role=`"button"`
 *
 * @returns { name, role } if a known role was found, or { name: description } with no role
 */
function parseDescription(description: string): { name: string; role?: string } {
  const trimmed = description.trim();
  if (!trimmed) return { name: '' };

  const lower = trimmed.toLowerCase();

  // Try suffix match: "<name> <role>" (most common: "Customer No. combobox")
  for (const role of KNOWN_ROLES) {
    if (lower.endsWith(' ' + role)) {
      const name = trimmed.slice(0, -(role.length + 1)).trim();
      if (name) return { name, role };
    }
  }

  // Try prefix match: "<role> <name>" (e.g., "button Save")
  for (const role of KNOWN_ROLES) {
    if (lower.startsWith(role + ' ')) {
      const name = trimmed.slice(role.length + 1).trim();
      if (name) return { name, role };
    }
  }

  // No role extracted — treat entire string as the name
  return { name: trimmed };
}

/**
 * Find an element in the snapshot matching a text description.
 *
 * Used when the agent clicks by description only (no ref, no coordinates).
 * Searches all elements with refs and scores them by how well they match
 * the description's role and name.
 *
 * Returns the best match **only if it is unique** at the highest score.
 * Returns `null` if no match, ambiguous (multiple matches at same score),
 * or the description is empty.
 *
 * @param snapshotYaml - The YAML-like snapshot string
 * @param description - Element description like "Customer No. combobox"
 * @returns Element info if a unique match is found, null otherwise
 */
export function findElementByDescription(
  snapshotYaml: string,
  description: string
): ElementInfo | null {
  if (!description?.trim()) return null;

  const { name, role } = parseDescription(description);
  if (!name) return null;

  const elements = extractAllElements(snapshotYaml);
  if (elements.length === 0) return null;

  const nameLower = name.toLowerCase();

  // Score each element
  const scored: Array<{ element: ElementInfo; score: number }> = [];

  for (const el of elements) {
    const elNameLower = (el.accessibleName ?? '').toLowerCase();
    const elRoleLower = (el.role ?? '').toLowerCase();

    let score = 0;

    if (role) {
      const roleMatches = elRoleLower === role;
      const nameExact = elNameLower === nameLower;
      const nameSubstring = elNameLower.includes(nameLower) || nameLower.includes(elNameLower);

      if (roleMatches && nameExact) {
        score = 100; // Exact role + name match
      } else if (roleMatches && nameSubstring && elNameLower.length > 0) {
        score = 80; // Role matches + name is substring
      }
    } else {
      // No role extracted — match on name only
      if (elNameLower === nameLower) {
        score = 60; // Name-only exact match
      } else if (elNameLower.includes(nameLower) || nameLower.includes(elNameLower)) {
        score = 50; // Name-only substring match
      } else {
        // Word-overlap: extract content words and check if any significant
        // description words appear in the element's accessible name.
        // Handles agent descriptions like "'Camera' sensor type tile (camera icon)"
        // matching element names like "Camera Edge or RTSP video source".
        const descWords = extractContentWords(nameLower);
        if (descWords.length > 0 && elNameLower.length > 0) {
          const elWords = new Set(extractContentWords(elNameLower));
          const matches = descWords.filter((w) => elWords.has(w));
          if (matches.length > 0) {
            score = 30 + Math.min(10, matches.length * 5); // 35-40 based on overlap
          }
        }
      }
    }

    if (score > 0) {
      scored.push({ element: el, score });
    }
  }

  if (scored.length === 0) return null;

  // Find highest score
  const maxScore = Math.max(...scored.map((s) => s.score));
  const topMatches = scored.filter((s) => s.score === maxScore);

  // Only return if the match is unique at the highest score level
  if (topMatches.length !== 1) return null;

  return topMatches[0].element;
}

/**
 * Common UI descriptor words that agents use but don't appear in accessible names.
 * Excluded from word-overlap matching to reduce false positives.
 */
const UI_DESCRIPTOR_WORDS = new Set([
  'tile',
  'icon',
  'card',
  'widget',
  'panel',
  'section',
  'area',
  'block',
  'element',
  'component',
  'item',
  'container',
  'wrapper',
  'field',
  'input',
  'type',
  'sensor',
  'option',
  'entry',
  'row',
  'column',
]);

/**
 * Extract significant content words from a description or element name.
 * Strips quotes, parenthetical suffixes, and common UI descriptor words.
 * Returns lowercase words of 3+ characters.
 */
function extractContentWords(text: string): string[] {
  // Strip parenthetical suffixes like "(camera icon)" and quoted segments like 'Camera'
  const cleaned = text
    .replace(/\([^)]*\)/g, ' ') // Remove parenthetical groups
    .replace(/['"]/g, ' '); // Remove quotes
  return cleaned
    .split(/\s+/)
    .map((w) => w.replace(/[^a-z0-9]/g, ''))
    .filter((w) => w.length >= 3 && !UI_DESCRIPTOR_WORDS.has(w));
}

/**
 * Check if a snapshot contains a specific ref.
 * Fast check without full parsing.
 * Handles both formats: [ref=e1] and [ref=e1 center=450,320]
 *
 * @param snapshotYaml - The YAML-like snapshot string
 * @param ref - The ref to check for
 * @returns true if ref exists in snapshot
 */
export function hasRef(snapshotYaml: string, ref: string): boolean {
  // Match [ref=X] or [ref=X center=...] format
  const pattern = new RegExp(`\\[ref=${ref}(?:\\s|\\])`);
  return pattern.test(snapshotYaml);
}

/**
 * Count the number of element refs in a snapshot.
 * Useful for diagnostics.
 * Handles both formats: [ref=e1] and [ref=e1 center=450,320]
 *
 * @param snapshotYaml - The YAML-like snapshot string
 * @returns Number of refs in the snapshot
 */
export function countRefs(snapshotYaml: string): number {
  // Match [ref=X] or [ref=X center=...] format
  const matches = snapshotYaml.match(/\[ref=\w+(?:\s+center=\d+,\d+)?\]/g);
  return matches?.length ?? 0;
}
